/**
 * ChatbotUI - Componente UI per chatbot con supporto streaming e allegati
 *
 * @class ChatbotUI
 * @description Classe per creare un'interfaccia chatbot completa con supporto per:
 * - Messaggi in tempo reale con streaming
 * - Allegati di file
 * - Conversazioni persistenti
 * - Formattazione Markdown
 * - Animazioni fluide
 *
 * @example
 * // ========================================
 * // UTILIZZO BASE
 * // ========================================
 *
 * // 1. Includi il file nella tua pagina HTML:
 * // <script src="js/lib/chatbot.js"></script>
 *
 * // 2. Inizializza il chatbot (configurazione minima):
 * const chatbot = new ChatbotUI({
 *   title: 'Il mio Assistente',
 *   apiEndpoint: '/api/chat'
 * });
 *
 * // 3. Avvia una nuova conversazione:
 * chatbot.startConversation();
 *
 * // Il chatbot è ora visibile come widget nell'angolo in basso a destra!
 *
 * @example
 * // ========================================
 * // CONFIGURAZIONE AVANZATA
 * // ========================================
 *
 * const chatbot = new ChatbotUI({
 *   title: 'Assistente Virtuale',
 *   position: 'bottom-right', // 'bottom-right', 'bottom-left', ecc.
 *   apiEndpoint: '/api/chat',
 *
 *   // Gestione personalizzata dell'invio messaggi
 *   onSendMessage: async (data) => {
 *     console.log('Invio messaggio:', data.text);
 *     console.log('ID conversazione:', data.conversationId);
 *     console.log('File allegato:', data.file);
 *
 *     // Invia i dati al tuo backend personalizzato
 *     const response = await fetch('/my-custom-endpoint', {
 *       method: 'POST',
 *       headers: { 'Content-Type': 'application/json' },
 *       body: JSON.stringify({
 *         message: data.text,
 *         conversationId: data.conversationId
 *       })
 *     });
 *
 *     const result = await response.json();
 *
 *     // Aggiungi la risposta manualmente
 *     chatbot.addMessage({
 *       author: 'assistant',
 *       content: result.reply,
 *       timestamp: new Date()
 *     });
 *   },
 *
 *   // Callback quando inizia una nuova conversazione
 *   onNewConversation: async (conversationId) => {
 *     console.log('Nuova conversazione creata:', conversationId);
 *     // Salva nel tuo database, analytics, ecc.
 *   },
 *
 *   // Callback quando si riprende una conversazione esistente
 *   onResumeConversation: async (conversationId) => {
 *     console.log('Conversazione ripresa:', conversationId);
 *     // Carica lo storico dal database
 *   }
 * });
 *
 * @example
 * // ========================================
 * // ESEMPIO COMPLETO DI PAGINA HTML
 * // ========================================
 *
 * // index.html:
 * // <!DOCTYPE html>
 * // <html lang="it">
 * // <head>
 * //   <meta charset="UTF-8">
 * //   <title>La mia app con Chatbot</title>
 * // </head>
 * // <body>
 * //   <h1>Benvenuto</h1>
 * //   <p>Il chatbot apparirà nell'angolo in basso a destra.</p>
 * //
 * //   <!-- Includi la libreria -->
 * //   <script src="js/lib/chatbot.js"></script>
 * //
 * //   <!-- Inizializza il chatbot -->
 * //   <script>
 * //     // Attendi il caricamento del DOM
 * //     document.addEventListener('DOMContentLoaded', () => {
 * //       const chatbot = new ChatbotUI({
 * //         title: 'Assistente Clienti',
 * //         apiEndpoint: '/api/chat'
 * //       });
 * //
 * //       // Avvia automaticamente una conversazione
 * //       chatbot.startConversation();
 * //     });
 * //   </script>
 * // </body>
 * // </html>
 *
 * @example
 * // ========================================
 * // GESTIONE CONVERSAZIONI PERSISTENTI
 * // ========================================
 *
 * const chatbot = new ChatbotUI({ title: 'Chat' });
 *
 * // Recupera conversazione salvata (es. da localStorage)
 * const savedConversationId = localStorage.getItem('chatConversationId');
 * const savedMessages = JSON.parse(localStorage.getItem('chatMessages') || '[]');
 *
 * if (savedConversationId && savedMessages.length > 0) {
 *   // Riprendi conversazione esistente
 *   chatbot.resumeConversation(savedConversationId, savedMessages);
 * } else {
 *   // Nuova conversazione
 *   chatbot.startConversation().then(conversationId => {
 *     localStorage.setItem('chatConversationId', conversationId);
 *   });
 * }
 *
 * // Salva messaggi quando cambiano (potresti usare un observer pattern)
 * setInterval(() => {
 *   const messages = chatbot.getMessages();
 *   localStorage.setItem('chatMessages', JSON.stringify(messages));
 * }, 5000); // Salva ogni 5 secondi
 *
 * @example
 * // ========================================
 * // CONTROLLO PROGRAMMATICO
 * // ========================================
 *
 * const chatbot = new ChatbotUI({ title: 'Support' });
 *
 * // Apri/chiudi la chat programmaticamente
 * document.getElementById('openChatBtn').addEventListener('click', () => {
 *   chatbot.toggleChat(); // Alterna apertura/chiusura
 * });
 *
 * // Chiudi esplicitamente
 * chatbot.closeChat();
 *
 * // Aggiungi un messaggio programmaticamente
 * chatbot.addMessage({
 *   author: 'assistant',
 *   content: 'Ciao! Come posso aiutarti?',
 *   timestamp: new Date()
 * });
 *
 * // Ottieni ID conversazione corrente
 * const convId = chatbot.getConversationId();
 *
 * // Ottieni tutti i messaggi
 * const messages = chatbot.getMessages();
 *
 * // Aggiorna intera conversazione
 * chatbot.updateConversation([
 *   { author: 'user', content: 'Ciao', timestamp: new Date() },
 *   { author: 'assistant', content: 'Salve!', timestamp: new Date() }
 * ]);
 *
 * @example
 * // ========================================
 * // FORMATO RISPOSTA SERVER (STREAMING)
 * // ========================================
 *
 * // Il server deve rispondere con Server-Sent Events (SSE)
 * // Content-Type: text/event-stream
 * //
 * // Formato dei messaggi:
 * // data: {"content": "Ciao"}
 * // data: {"content": " come"}
 * // data: {"content": " va?"}
 * // data: [DONE]
 *
 * // Esempio server Node.js/Express:
 * // app.post('/api/chat', (req, res) => {
 * //   res.setHeader('Content-Type', 'text/event-stream');
 * //   res.setHeader('Cache-Control', 'no-cache');
 * //   res.setHeader('Connection', 'keep-alive');
 * //
 * //   const message = 'Questa è la risposta';
 * //   const words = message.split(' ');
 * //
 * //   let i = 0;
 * //   const interval = setInterval(() => {
 * //     if (i < words.length) {
 * //       res.write(`data: ${JSON.stringify({content: words[i] + ' '})}\n\n`);
 * //       i++;
 * //     } else {
 * //       res.write('data: [DONE]\n\n');
 * //       res.end();
 * //       clearInterval(interval);
 * //     }
 * //   }, 100);
 * // });
 *
 * @example
 * // ========================================
 * // FORMATO RISPOSTA SERVER (NO STREAMING)
 * // ========================================
 *
 * // Se non vuoi usare lo streaming, rispondi con JSON normale:
 * // Content-Type: application/json
 * //
 * // { "message": "Questa è la risposta completa" }
 * // oppure
 * // { "content": "Questa è la risposta completa" }
 *
 * // Esempio server:
 * // app.post('/api/chat', (req, res) => {
 * //   res.json({ message: 'Risposta del server' });
 * // });
 *
 * @example
 * // ========================================
 * // SUPPORTO MARKDOWN E FORMATTAZIONE
 * // ========================================
 *
 * // Il chatbot supporta automaticamente:
 * // - **grassetto**
 * // - *corsivo*
 * // - `codice inline`
 * // - ```blocchi di codice```
 * // - [link](https://example.com)
 * // - ![immagine](url.jpg)
 * // - URL diretti vengono convertiti in link
 *
 * chatbot.addMessage({
 *   author: 'assistant',
 *   content: 'Ecco un esempio con **grassetto** e `codice`!\n\nVisita [Google](https://google.com)',
 *   timestamp: new Date()
 * });
 */
class ChatbotUI {
    /**
     * Crea una nuova istanza di ChatbotUI
     * @param {Object} options - Opzioni di configurazione
     * @param {string} [options.title='Chatbot'] - Titolo visualizzato nell'header
     * @param {string} [options.subtitle=''] - Sottotitolo visualizzato sotto il title
     * @param {string} [options.position='bottom-right'] - Posizione del widget
     * @param {string} [options.apiEndpoint='/api/chat'] - Endpoint API per l'invio messaggi
     * @param {string} [options.name] - Nome visualizzato come autore dei messaggi del chatbot (assistant)
     * @param {Function} [options.onSendMessage=null] - Callback personalizzato per l'invio messaggi
     * @param {Function} [options.onNewConversation=null] - Callback per nuove conversazioni
     * @param {Function} [options.onResumeConversation=null] - Callback per riprendere conversazioni
     * @param {boolean} [options.allowAttachments=true] - Abilita o disabilita la possibilità di allegare file
     * @param {string} [options.allowedFileTypes=null] - Tipi di file accettati (es. '.jpg,.png' o 'image/*,application/pdf')
     * @param {number} [options.maxFileSize=null] - Dimensione massima dei file in bytes
     */
    constructor(options = {}) {
        // Determina la lingua da usare
        const getUserLanguage = () => {
            // 1. Controlla se l'utente ha specificato una lingua
            if (options.language) {
                return options.language;
            }

            // 2. Prova a ottenere la lingua del browser
            const browserLang = navigator.language || navigator.language;
            if (browserLang) {
                return browserLang.substring(0, 2);
            }

            // 3. Fallback su inglese
            return 'en';
        };

        const userLanguage = getUserLanguage();

        this.options = {
            title: options.title || 'Chatbot',
            subtitle: options.subtitle || '',
            position: options.position || 'bottom-right',
            apiEndpoint: options.apiEndpoint || '/api/chat',
            language: userLanguage,
            name: typeof options.name === 'string' ? options.name.trim() : '',
            onSendMessage: options.onSendMessage || null,
            onNewConversation: options.onNewConversation || null,
            onResumeConversation: options.onResumeConversation || null,
            allowAttachments: options.allowAttachments !== undefined ? options.allowAttachments : true,
            allowedFileTypes: options.allowedFileTypes || null,
            maxFileSize: options.maxFileSize || null,
            showPoweredBy: options.showPoweredBy !== undefined ? options.showPoweredBy : true,
            ...options
        };

        /** @type {string|null} ID univoco della conversazione corrente */
        this.conversationId = null;

        /** @type {Array} Array dei messaggi della conversazione */
        this.messages = [];

        /** @type {boolean} Indica se la finestra chat è aperta */
        this.isOpen = false;

        /** @type {boolean} Indica se è in corso uno streaming di risposta */
        this.isStreaming = false;

        /** @type {Object|null} Riferimento al messaggio attualmente in streaming */
        this.currentStreamingMessage = null;

        /** @type {boolean} Indica se l'istanza è anonima (senza conversationId generato localmente) */
        this.isAnonymous = false;

        /** @type {boolean} Dropdown download aperto */
        this.isDownloadMenuOpen = false;

        /** @type {Object} Traduzioni per l'interfaccia del chatbot */
        this.i18n = {
            'en': {
                author: 'assistant',
                inputPlaceholder: 'Write a message...',
                fileButtonTitle: 'Attach file',
                fileInputAriaLabel: 'Attach a file',
                submitButtonTitle: 'Send',
                toggleButtonAriaLabel: 'Open or close chat',
                closeButtonAriaLabel: 'Close chat',
                removeFileButtonLabel: 'Remove',
                userAuthorName: 'You',
                assistantAuthorName: 'Assistant',
                errorMessage: 'An error occurred. Please try again later.',
                expandButtonAriaLabel: 'Expand or collapse chat',
                downloadButtonTitle: 'Download chat',
                downloadButtonAriaLabel: 'Download chat',
                downloadPdfLabel: 'PDF (readable)',
                downloadCsvLabel: 'CSV (data)',
                downloadJsonLabel: 'JSON (dump)',
                fileSizeError: 'File too large. Maximum size is ',
                fileTypeError: 'File type not allowed. Accepted types: ',
                maxFileSize: 'Max: ',
                allowedTypes: 'Types: '
            },
            'it': {
                author: 'assistant',
                inputPlaceholder: 'Scrivi un messaggio...',
                fileButtonTitle: 'Allega file',
                fileInputAriaLabel: 'Allega un file',
                submitButtonTitle: 'Invia',
                toggleButtonAriaLabel: 'Apri o chiudi la chat',
                closeButtonAriaLabel: 'Chiudi chat',
                removeFileButtonLabel: 'Rimuovi',
                userAuthorName: 'Tu',
                assistantAuthorName: 'Assistente',
                errorMessage: 'Si è verificato un errore. Riprova più tardi.',
                expandButtonAriaLabel: 'Espandi o riduci chat',
                downloadButtonTitle: 'Scarica chat',
                downloadButtonAriaLabel: 'Scarica chat',
                downloadPdfLabel: 'PDF (leggibile)',
                downloadCsvLabel: 'CSV (dati)',
                downloadJsonLabel: 'JSON (dump)',
                fileSizeError: 'File troppo grande. La dimensione massima è ',
                fileTypeError: 'Tipo di file non permesso. Tipi accettati: ',
                maxFileSize: 'Max: ',
                allowedTypes: 'Tipi: '
            },
            'es': {
                author: 'assistant',
                inputPlaceholder: 'Escribe un mensaje...',
                fileButtonTitle: 'Adjuntar archivo',
                fileInputAriaLabel: 'Adjuntar un archivo',
                submitButtonTitle: 'Enviar',
                toggleButtonAriaLabel: 'Abrir o cerrar el chat',
                closeButtonAriaLabel: 'Cerrar chat',
                removeFileButtonLabel: 'Eliminar',
                userAuthorName: 'Tú',
                assistantAuthorName: 'Asistente',
                errorMessage: 'Se ha producido un error. Inténtalo de nuevo más tarde.',
                expandButtonAriaLabel: 'Expandir o contraer el chat',
                downloadButtonTitle: 'Descargar chat',
                downloadButtonAriaLabel: 'Descargar chat',
                downloadPdfLabel: 'PDF (legible)',
                downloadCsvLabel: 'CSV (datos)',
                downloadJsonLabel: 'JSON (volcado)',
                fileSizeError: 'Archivo demasiado grande. El tamaño máximo es ',
                fileTypeError: 'Tipo de archivo no permitido. Tipos aceptados: ',
                maxFileSize: 'Máx.: ',
                allowedTypes: 'Tipos: '
            },
            'fr': {
                author: 'assistant',
                inputPlaceholder: 'Écrivez un message...',
                fileButtonTitle: 'Joindre un fichier',
                fileInputAriaLabel: 'Joindre un fichier',
                submitButtonTitle: 'Envoyer',
                toggleButtonAriaLabel: 'Ouvrir ou fermer le chat',
                closeButtonAriaLabel: 'Fermer le chat',
                removeFileButtonLabel: 'Supprimer',
                userAuthorName: 'Vous',
                assistantAuthorName: 'Assistant',
                errorMessage: 'Une erreur s’est produite. Réessayez plus tard.',
                expandButtonAriaLabel: 'Agrandir ou réduire le chat',
                downloadButtonTitle: 'Télécharger le chat',
                downloadButtonAriaLabel: 'Télécharger le chat',
                downloadPdfLabel: 'PDF (lisible)',
                downloadCsvLabel: 'CSV (données)',
                downloadJsonLabel: 'JSON (export brut)',
                fileSizeError: 'Fichier trop volumineux. La taille maximale est ',
                fileTypeError: 'Type de fichier non autorisé. Types acceptés : ',
                maxFileSize: 'Max : ',
                allowedTypes: 'Types : '
            },
            'de': {
                author: 'assistant',
                inputPlaceholder: 'Schreibe eine Nachricht...',
                fileButtonTitle: 'Datei anhängen',
                fileInputAriaLabel: 'Eine Datei anhängen',
                submitButtonTitle: 'Senden',
                toggleButtonAriaLabel: 'Chat öffnen oder schließen',
                closeButtonAriaLabel: 'Chat schließen',
                removeFileButtonLabel: 'Entfernen',
                userAuthorName: 'Du',
                assistantAuthorName: 'Assistent',
                errorMessage: 'Ein Fehler ist aufgetreten. Bitte versuche es später erneut.',
                expandButtonAriaLabel: 'Chat erweitern oder verkleinern',
                downloadButtonTitle: 'Chat herunterladen',
                downloadButtonAriaLabel: 'Chat herunterladen',
                downloadPdfLabel: 'PDF (lesbar)',
                downloadCsvLabel: 'CSV (Daten)',
                downloadJsonLabel: 'JSON (Dump)',
                fileSizeError: 'Datei zu groß. Die maximale Größe ist ',
                fileTypeError: 'Dateityp nicht erlaubt. Akzeptierte Typen: ',
                maxFileSize: 'Max: ',
                allowedTypes: 'Typen: '
            }
        };

        /** @type {string} Lingua da usare per la traduzione dell'interfaccia del chatbot */
        this.currentLanguage = this.i18n[this.options.language] ? this.options.language : 'en';

        /** @type {string|null} ID del messaggio "in attesa" (non-streaming) */
        this.pendingMessageId = null;

        this.init();
    }

    /**
     * Inizializza il componente chatbot
     * @private
     */
    init() {
        this.injectStyles();
        this.createChatbotHTML();
        this.attachEventListeners();
    }

    /**
     * Inietta gli stili CSS necessari per il chatbot nel documento
     * @private
     */
    injectStyles() {
        const styleId = 'chatbot-styles';
        if (document.getElementById(styleId)) return;

        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = `
                    .chatbot-container {
                        z-index: 9999;
                        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                        display: flex;
                        flex-direction: column;
                        align-items: flex-end;
                        max-width: calc(100vw - 16px);
                        max-height: calc(100dvh - 16px);
                        position: fixed;
                    }

                    .chatbot-toggle {
                        width: 60px;
                        height: 60px;
                        border-radius: 50%;
                        background: #000;
                        border: none;
                        cursor: pointer;
                        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        color: white;
                        font-size: 24px;
                        transition: transform 0.2s;
                        flex: 0 0 auto;
                        position: fixed;
                        right: 20px;
                        bottom: 20px;
                    }

                    .chatbot-toggle::after {
                        content: url("data:image/svg+xml;utf8,<svg viewBox='0 0 24 24' width='32' height='32' xmlns='http://www.w3.org/2000/svg'><path fill='white' d='M12 3c5.5 0 10 3.58 10 8s-4.5 8-10 8c-1.24 0-2.43-.18-3.53-.5C5.55 21 2 21 2 21c2.33-2.33 2.7-3.9 2.75-4.5C3.05 15.07 2 13.13 2 11c0-4.42 4.5-8 10-8'/></svg>");
                        line-height: 1;
                    }

                    .chatbot-toggle:hover {
                        transform: scale(1.1);
                    }

                    .chatbot-window {
                        width: 380px;
                        height: 600px;
                        max-width: 90%;
                        max-height: calc(100dvh - 84px - 16px);
                        background: white;
                        border-radius: 12px;
                        box-shadow: 0 8px 32px rgba(0,0,0,0.2);
                        display: none;
                        flex-direction: column;
                        overflow: hidden;
                        margin-bottom: 12px;
                        position: fixed;
                        right: 20px;
                        bottom: 80px;
                    }

                    .chatbot-window.open {
                        display: flex;
                    }

                    .chatbot-window.expanded {
                        width: min(100vw - 16px, 1100px);
                        max-width: calc(100vw - 16px);
                    }

                    .chatbot-header {
                        background: #000;
                        color: white;
                        padding: 16px;
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        flex: 0 0 auto;
                    }

                    .chatbot-header h3 {
                        margin: 0;
                        font-size: 18px;
                        font-weight: 600;
                    }

                    .chatbot-header-actions {
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        position: relative;
                    }

                    .chatbot-expand,
                    .chatbot-close {
                        background: none;
                        border: none;
                        color: white;
                        font-size: 24px;
                        cursor: pointer;
                        padding: 0;
                        width: 32px;
                        height: 32px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        border-radius: 4px;
                        transition: background 0.2s;
                    }

                    .chatbot-expand:hover,
                    .chatbot-close:hover {
                        background: rgba(255,255,255,0.2);
                    }

                    .chatbot-download {
                        background: none;
                        border: none;
                        color: white;
                        font-size: 18px;
                        cursor: pointer;
                        padding: 0;
                        width: 32px;
                        height: 32px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        border-radius: 4px;
                        transition: background 0.2s;
                    }

                    .chatbot-download:hover {
                        background: rgba(255,255,255,0.2);
                    }

                    .chatbot-download:disabled {
                        opacity: 0.6;
                        cursor: not-allowed;
                    }

                    .chatbot-download-menu {
                        position: absolute;
                        top: calc(100% + 8px);
                        right: 0;
                        min-width: 220px;
                        max-width: min(280px, calc(100vw - 24px));
                        background: white;
                        border: 1px solid rgba(0,0,0,0.12);
                        border-radius: 10px;
                        box-shadow: 0 10px 30px rgba(0,0,0,0.18);
                        padding: 6px;
                        display: none;
                        z-index: 10000;
                    }

                    .chatbot-download-menu.open {
                        display: block;
                    }

                    .chatbot-download-item {
                        width: 100%;
                        text-align: left;
                        border: none;
                        background: transparent;
                        padding: 10px 10px;
                        border-radius: 8px;
                        cursor: pointer;
                        font-size: 13px;
                        color: #212529;
                    }

                    .chatbot-download-item:hover {
                        background: #f1f3f5;
                    }

                    .chatbot-download-item:disabled {
                        opacity: 0.6;
                        cursor: not-allowed;
                    }

                    .chatbot-messages {
                        flex: 1 1 auto;
                        min-height: 0;
                        overflow-y: auto;
                        padding: 16px;
                        background: #f8f9fa;
                    }

                    .chatbot-message {
                        margin-bottom: 16px;
                        animation: slideIn 0.3s ease-out;
                    }

                    @keyframes slideIn {
                        from { opacity: 0; transform: translateY(10px); }
                        to { opacity: 1; transform: translateY(0); }
                    }

                    .chatbot-message-header {
                        display: flex;
                        justify-content: space-between;
                        margin-bottom: 4px;
                        font-size: 12px;
                    }

                    .chatbot-message-author {
                        font-weight: 600;
                        color: #495057;
                    }

                    .chatbot-message-time {
                        color: #6c757d;
                    }

                    .chatbot-message-content {
                        background: white;
                        padding: 12px;
                        border-radius: 8px;
                        box-shadow: 0 1px 2px rgba(0,0,0,0.05);
                        line-height: 1.5;
                        word-wrap: break-word;
                    }

                    .chatbot-message-content p {
                        margin-bottom: 1em;
                    }

                    .chatbot-message-content a {
                        text-decoration: underline;
                        color: #333;
                    }

                    .chatbot-message-content img {
                        max-width: 100px;
                    }

                    .chatbot-message.user .chatbot-message-content {
                        background: #e2e3e5;
                    }

                    .chatbot-input-area {
                        border-top: 1px solid #dee2e6;
                        padding: 0;
                        background: white;
                        flex: 0 0 auto;
                    }

                    .chatbot-input-form {
                        display: flex;
                        flex-wrap: wrap;
                        gap: 8px;
                        padding: 0.75rem;
                        margin: 0;
                    }

                    .chatbot-input-wrapper {
                        flex: 1 0 100%;
                        display: flex;
                        flex-wrap: wrap;
                        gap: 8px;
                        align-items: flex-end;
                    }

                    .chatbot-input {
                        flex: 1;
                        border: 1px solid #ced4da;
                        border-radius: 8px;
                        padding: 10px 12px;
                        font-size: 14px;
                        resize: none;
                        max-height: 70px;
                        font-family: inherit;
                        min-width: 0;
                    }

                    .chatbot-input:focus {
                        outline: none;
                        border-color: #333;
                    }

                    .chatbot-file-input {
                        display: none;
                    }

                    .chatbot-file-button {
                        background: #f8f9fa;
                        border: 1px solid #333;
                        border-radius: 8px;
                        padding: 10px 12px;
                        cursor: pointer;
                        transition: background 0.2s;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        min-width: 44px;
                    }

                    .chatbot-file-button:hover {
                        background: #e9ecef;
                    }

                    .chatbot-send-button {
                        background: #000;
                        color: white;
                        border: none;
                        border-radius: 8px;
                        padding: 13px 20px;
                        cursor: pointer;
                        font-weight: 600;
                        transition: background 0.2s;
                        white-space: nowrap;
                    }

                    .chatbot-send-button:hover {
                        background: #000;
                    }

                    .chatbot-send-button:disabled {
                        background: #6c757d;
                        cursor: not-allowed;
                    }

                    .chatbot-file-preview {
                        margin: 8px 12px 12px;
                        padding: 8px;
                        background: #f8f9fa;
                        border-radius: 4px;
                        font-size: 12px;
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        gap: 8px;
                    }

                    .chatbot-file-remove {
                        background: none;
                        border: none;
                        color: #dc3545;
                        cursor: pointer;
                        font-weight: 600;
                    }

                    .chatbot-streaming-indicator {
                        display: inline-block;
                        width: 8px;
                        height: 8px;
                        background: #6c757d;
                        border-radius: 50%;
                        animation: pulse 1.5s infinite;
                    }

                    @keyframes pulse {
                        0%, 100% { opacity: 1; }
                        50% { opacity: 0.3; }
                    }

                    .gc-chat-suggestion {
                        display:block;
                        width:100%;
                        text-align:left;
                        padding:10px 12px;
                        margin:6px 0;
                        border:1px solid #333;
                        border-radius:10px;
                        background:#fff;
                        cursor:pointer;
                        font-size:14px;
                        text-decoration:none;
                        color:inherit;
                    }

                    .chatbot-poweredby {
                        text-align: right;
                        margin-left: auto;
                        font-size: 10px;
                        font-weight: bold;
                        line-height: 1.3;
                        margin-bottom: 5px;
                        flex-direction: row;
                        display: flex;
                        align-items: center;
                    }

                    .chatbot-poweredby img {
                        width: 79px;
                        margin-left: auto;
                        object-fit: contain;
                        display: block;
                    }
                `;
        document.head.appendChild(style);
    }

    /**
     * Ritorna una stringa tradotta in base a this.currentLanguage
     * @param {string} key
     * @param {string} [fallback='']
     * @returns {string}
     */
    i18nTranslation(key, fallback = '') {
        const dict = this.i18n[this.currentLanguage] || this.i18n['en'] || {};
        return (dict[key] != null ? dict[key] : fallback).toString();
    }

    /**
     * Nome da visualizzare come autore dei messaggi del chatbot (assistant).
     * Se l'utente fornisce options.name, questo ha la precedenza sul default i18n.
     * @returns {string}
     * @private
     */
    getAssistantDisplayName() {
        return this.options.name || this.i18nTranslation('assistantAuthorName', 'Assistant');
    }

    /**
     * Crea la struttura HTML del chatbot e la inserisce nel DOM
     * @private
     */
    createChatbotHTML() {
        const container = document.createElement('div');
        container.className = 'chatbot-container';
        container.innerHTML = `
                        <button class="chatbot-toggle" title="chat" aria-label="${this.i18nTranslation('toggleButtonAriaLabel', 'Toggle chat')}">
                        </button>
                        <div class="chatbot-window">
                            <div class="chatbot-header">
                                <div class="chatbot-header-info">
                                    <h3>${this.options.title}</h3>
                                    ${this.options.subtitle ? `<div class="chatbot-header-subtitle">${this.options.subtitle}</div>` : ''}
                                </div>
                                <div class="chatbot-header-actions">
                                    <button class="chatbot-download"
                                            title="${this.i18nTranslation('downloadButtonTitle', 'Download chat')}"
                                            aria-label="${this.i18nTranslation('downloadButtonAriaLabel', 'Download chat')}"
                                            type="button">⬇</button>
                                    <div class="chatbot-download-menu" role="menu" aria-hidden="true">
                                        <button type="button" class="chatbot-download-item" data-format="pdf" role="menuitem">
                                            ${this.i18nTranslation('downloadPdfLabel', 'PDF (readable)')}
                                        </button>
                                        <button type="button" class="chatbot-download-item" data-format="csv" role="menuitem">
                                            ${this.i18nTranslation('downloadCsvLabel', 'CSV (data)')}
                                        </button>
                                        <button type="button" class="chatbot-download-item" data-format="json" role="menuitem">
                                            ${this.i18nTranslation('downloadJsonLabel', 'JSON (dump)')}
                                        </button>
                                    </div>
                                    <button class="chatbot-expand" aria-label="${this.i18nTranslation('expandButtonAriaLabel', 'Expand or collapse chat')}">⤢</button>
                                    <button class="chatbot-close" aria-label="${this.i18nTranslation('closeButtonAriaLabel', 'Close chat')}">×</button>
                                </div>
                            </div>
                            <div class="chatbot-messages"></div>
                            <div class="chatbot-input-area">
                                <form class="chatbot-input-form">
                                <div class="chatbot-input-wrapper">
                                    <textarea 
                                        class="chatbot-input" 
                                        placeholder="${this.i18nTranslation('inputPlaceholder', 'Write a message...')}"
                                        rows="3"
                                    ></textarea>
                                </div>
                                <div class="chatbot-input-wrapper">
                                    ${this.options.allowAttachments ? `
                                    <label class="chatbot-file-button" title="${this.getFileButtonTooltip()}">
                                        📎
                                        <input type="file" class="chatbot-file-input" aria-label="${this.i18nTranslation('fileInputAriaLabel', 'Attach a file')}" ${this.options.allowedFileTypes ? `accept="${this.options.allowedFileTypes}"` : ''} />
                                    </label>
                                    ` : ''}
                                    <button type="submit" class="chatbot-send-button">${this.i18nTranslation('submitButtonTitle', 'Send')}</button>
                                    ${this.options.showPoweredBy ? `
                                    <span class="chatbot-poweredby">
                                        <a href="https://efashionpro.com/" target="_blank">
                                            powered by
                                            <img src="https://efashionpro.com/cdn/shop/files/logo_Efashion_pro_black.png" alt="eFashion Pro" width="79" height="16">
                                        </a>
                                    </span>
                                    ` : ''}
                                </div>
                                </form>
                                <div class="chatbot-file-preview" style="display: none;">
                                    <span class="chatbot-file-name"></span>
                                    <button type="button" class="chatbot-file-remove">${this.i18nTranslation('removeFileButtonLabel', 'Remove')}</button>
                                </div>
                            </div>
                        </div>
                    `;

        document.body.appendChild(container);

        /** @type {Object} Riferimenti agli elementi DOM del chatbot */
        this.elements = {
            container,
            toggle: container.querySelector('.chatbot-toggle'),
            window: container.querySelector('.chatbot-window'),
            close: container.querySelector('.chatbot-close'),
            expand: container.querySelector('.chatbot-expand'),
            messages: container.querySelector('.chatbot-messages'),
            form: container.querySelector('.chatbot-input-form'),
            input: container.querySelector('.chatbot-input'),
            fileInput: container.querySelector('.chatbot-file-input'),
            fileButton: container.querySelector('.chatbot-file-button'),
            filePreview: container.querySelector('.chatbot-file-preview'),
            fileName: container.querySelector('.chatbot-file-name'),
            fileRemove: container.querySelector('.chatbot-file-remove'),
            sendButton: container.querySelector('.chatbot-send-button'),
            downloadButton: container.querySelector('.chatbot-download'),
            downloadMenu: container.querySelector('.chatbot-download-menu'),
            downloadItems: Array.prototype.slice.call(container.querySelectorAll('.chatbot-download-item'))
        };
    }

    /**
     * Collega gli event listener agli elementi interattivi
     * @private
     */
    attachEventListeners() {
        this.elements.toggle.addEventListener('click', () => this.toggleChat());
        this.elements.close.addEventListener('click', () => this.closeChat());
        this.elements.expand.addEventListener('click', () => this.toggleExpanded());
        this.elements.form.addEventListener('submit', (e) => this.handleSubmit(e));
        if (this.options.allowAttachments) {
            this.elements.fileInput.addEventListener('change', (e) => this.handleFileSelect(e));
            this.elements.fileRemove.addEventListener('click', () => this.removeFile());
        }

        // Download dropdown
        this.elements.downloadButton.addEventListener('click', (e) => {
            e.preventDefault();
            if (this.elements.downloadButton.disabled) return;
            this.toggleDownloadMenu();
        });

        this.elements.downloadItems.forEach((btn) => {
            btn.addEventListener('click', async (e) => {
                e.preventDefault();
                if (btn.disabled) return;
                const format = btn.getAttribute('data-format');
                this.closeDownloadMenu();

                if (format === 'pdf') this.downloadChatAsPdf();
                if (format === 'csv') this.downloadChatAsCsv();
                if (format === 'json') this.downloadChatAsJson();
            });
        });

        // Chiudi menu cliccando fuori / ESC
        document.addEventListener('click', (e) => {
            if (!this.isDownloadMenuOpen) return;
            const within = this.elements.downloadMenu.contains(e.target) || this.elements.downloadButton.contains(e.target);
            if (!within) this.closeDownloadMenu();
        });

        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') this.closeDownloadMenu();
        });

        // Auto-ridimensionamento della textarea
        this.elements.input.addEventListener('input', () => {
            this.elements.input.style.height = 'auto';
            this.elements.input.style.height = this.elements.input.scrollHeight + 'px';
        });

        // Invio con Enter (Shift+Enter per nuova riga)
        this.elements.input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                this.elements.form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
            }
        });

        // Stato iniziale: abilitato
        this.setInputsEnabled(true);
    }

    /**
     * Alterna la visibilità della finestra chat
     */
    toggleChat() {
        this.isOpen = !this.isOpen;
        this.elements.window.classList.toggle('open', this.isOpen);

        if (this.isOpen) {
            this.elements.input.focus();
        }
    }

    /**
     * Alterna lo stato "expanded" della finestra chat.
     */
    toggleExpanded() {
        this.elements.window.classList.toggle('expanded');
    }

    /**
     * Chiude la finestra chat
     */
    closeChat() {
        this.isOpen = false;
        this.elements.window.classList.remove('open');
        this.elements.window.classList.remove('expanded');
        this.closeDownloadMenu();
    }

    /**
     * Gestisce la selezione di un file
     * @param {Event} e - Evento change dell'input file
     * @private
     */
    handleFileSelect(e) {
        const file = e.target.files[0];
        if (!file) return;

        if (this.options.maxFileSize && file.size > this.options.maxFileSize) {
            alert(this.i18nTranslation('fileSizeError', 'File troppo grande. La dimensione massima è ') + this.formatFileSize(this.options.maxFileSize));
            this.elements.fileInput.value = '';
            return;
        }

        if (this.options.allowedFileTypes) {
            const allowedExtensions = this.options.allowedFileTypes.split(',').map(t => t.trim().toLowerCase());
            const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
            const fileMime = file.type.toLowerCase();
            
            const isAllowed = allowedExtensions.some(allowed => {
                if (allowed.startsWith('.')) {
                    return fileExtension === allowed;
                } else if (allowed.includes('/*')) {
                    const baseType = allowed.split('/*')[0];
                    return fileMime.startsWith(baseType + '/');
                } else {
                    return fileMime === allowed;
                }
            });

            if (!isAllowed) {
                alert(this.i18nTranslation('fileTypeError', 'Tipo di file non permesso. Tipi accettati: ') + this.options.allowedFileTypes);
                this.elements.fileInput.value = '';
                return;
            }
        }

        this.selectedFile = file;
        this.elements.fileName.textContent = file.name;
        this.elements.filePreview.style.display = 'flex';
    }

    /**
     * Rimuove il file selezionato
     */
    removeFile() {
        this.selectedFile = null;
        this.elements.fileInput.value = '';
        this.elements.filePreview.style.display = 'none';
    }

    /**
     * Abilita o disabilita gli input del chatbot
     * @param {boolean} enabled - true per abilitare, false per disabilitare
     * @private
     */
    setInputsEnabled(enabled){
        this.elements.input.disabled = !enabled;
        if (this.elements.fileInput) {
            this.elements.fileInput.disabled = !enabled;
        }
        this.elements.sendButton.disabled = !enabled;
        if (this.elements.fileButton) {
            this.elements.fileButton.style.pointerEvents = enabled ? '' : 'none';
            this.elements.fileButton.style.opacity = enabled ? '' : '0.5';
        }

        // Disabilita anche il download mentre si attende risposta (come richiesto)
        if (this.elements.downloadButton) {
            this.elements.downloadButton.disabled = !enabled;
        }
        if (this.elements.downloadItems && this.elements.downloadItems.length) {
            this.elements.downloadItems.forEach((b) => { b.disabled = !enabled; });
        }
        if (!enabled) this.closeDownloadMenu();
    }

    /**
     * Gestisce l'invio del form
     * @param {Event} e - Evento submit del form
     * @private
     */
    async handleSubmit(e){
        e.preventDefault();

        const text = this.elements.input.value.trim();
        if (!text && !this.selectedFile) return;

        // Disabilita gli input durante l'invio
        this.setInputsEnabled(false);

        const message = {
            author: 'user',
            content: text,
            timestamp: new Date(),
            file: this.selectedFile
        };

        this.addMessage(message);
        this.elements.input.value = '';
        this.elements.input.style.height = 'auto';

        const file = this.selectedFile;
        this.removeFile();

        try {
            await this.sendMessage(text, file);
        } finally {
            // Riabilita gli input dopo la risposta
            this.setInputsEnabled(true);
        }
    }

    /**
     * Aggiunge un messaggio alla conversazione
     * @param {Object} message - Oggetto messaggio
     */
    addMessage(message) {
        this.messages.push(message);
        this.renderMessage(message);
        this.scrollToBottom();
    }

    /**
     * Renderizza un messaggio nel DOM
     * @param {Object} message - Oggetto messaggio da renderizzare
     * @returns {HTMLElement} Elemento DOM del messaggio creato
     * @private
     */
    renderMessage(message) {
        const messageEl = document.createElement('div');
        messageEl.className = `chatbot-message ${message.author}`;

        const timeStr = this.formatTime(message.timestamp);
        const authorName =
            message.author === 'user'
                ? this.i18nTranslation('userAuthorName', 'You')
                : this.getAssistantDisplayName();

        const showIndicator = Boolean(message.streaming || message.pending);
        const indicatorHtml = showIndicator ? ' <span class="chatbot-streaming-indicator"></span>' : '';

        messageEl.innerHTML = `
                            <div class="chatbot-message-header">
                                <span class="chatbot-message-author">${authorName}</span>
                                <span class="chatbot-message-time">${timeStr}</span>
                            </div>
                            <div class="chatbot-message-content">${this.parseContent(message.content)}${indicatorHtml}</div>
                        `;

        this.elements.messages.appendChild(messageEl);

        // Permette update in-place sia per streaming che per pending
        if (message.streaming || message.pending) {
            messageEl.dataset.messageId = message.id;
        }

        return messageEl;
    }

    /**
     * Aggiorna il contenuto HTML di un messaggio già renderizzato.
     */
    updateMessageContent(messageId, content, opts = {}) {
        const { showIndicator = false, streaming = false } = opts;

        //DEBUG
        //const allMessageEls = this.elements.messages.querySelectorAll('[data-message-id]');
        //console.log(`[Chatbot] updateMessageContent called:`);
        //console.log(`  - messageId: ${messageId}`);
        //console.log(`  - streaming: ${streaming}`);
        //console.log(`  - Total messages with data-message-id: ${allMessageEls.length}`);
        //allMessageEls.forEach((el, i) => {
        //    console.log(`  - Message ${i}: data-message-id="${el.dataset.messageId}"`);
        //});

        const messageEl = this.elements.messages.querySelector(`[data-message-id="${messageId}"]`);
        if (!messageEl) {
            //DEBUG console.log(`[Chatbot] ERROR: No message found with messageId: ${messageId}`);
            return;
        }
        //DEBUG console.log(`[Chatbot] Found message, updating content...`);

        const contentEl = messageEl.querySelector('.chatbot-message-content');
        const indicatorHtml = showIndicator ? ' <span class="chatbot-streaming-indicator"></span>' : '';
        contentEl.innerHTML = this.parseContent(content) + indicatorHtml;

        this.scrollToBottom();
    }

    /**
     * Converte il contenuto Markdown in HTML
     * Supporta: grassetto, corsivo, code inline, code blocks, immagini, link
     * @param {string} content - Contenuto in formato Markdown
     * @returns {string} Contenuto convertito in HTML
     * @private
     */
    parseContent(content) {
        if (!content) return '';

        let parsed = content;
        const placeholders = new Map();
        let placeholderIndex = 0;

        const savePlaceholder = (html) => {
            const key = `__PLACEHOLDER_${placeholderIndex}__`;
            placeholderIndex++;
            placeholders.set(key, html);
            return key;
        };

        const restorePlaceholders = (text) => {
            let result = text;
            placeholders.forEach((value, key) => {
                result = result.replace(new RegExp(key, 'g'), value);
            });
            return result;
        };

        const isInsideTag = (text, index) => {
            const before = text.substring(0, index);
            const openTags = (before.match(/</g) || []).length;
            const closeTags = (before.match(/>/g) || []).length;
            return openTags > closeTags;
        };

        // Grassetto
        parsed = parsed.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

        // Corsivo
        parsed = parsed.replace(/\*(.*?)\*/g, '<em>$1</em>');

        // Codice inline
        parsed = parsed.replace(/`([^`]+)`/g, '<code>$1</code>');

        // Blocchi di codice
        parsed = parsed.replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>');

        // Immagini linkate Markdown: [![alt](img_url)](link_url)
        parsed = parsed.replace(/\[!\[([^\]]*)\]\(([^)\n\\]+)\)\]\(([^)\n\\]+)\)/g, (match, alt, imgUrl, linkUrl) => {
            return savePlaceholder(`<a href="${linkUrl}" target="_blank"><img src="${imgUrl}" alt="${alt}" /></a>`);
        });

        // Immagini normali: ![alt](url)
        parsed = parsed.replace(/!\[([^\]]*)\]\(([^)\n\\]+)\)/g, (match, alt, url) => {
            return savePlaceholder(`<img src="${url}" alt="${alt}" />`);
        });

        // Link: [text](url)
        parsed = parsed.replace(/\[([^\]]+)\]\(([^)\n\\]+)\)/g, (match, text, url) => {
            return savePlaceholder(`<a href="${url}" target="_blank">${text}</a>`);
        });

        // URL immagini diretti (matcha solo se non dentro tag HTML)
        parsed = parsed.replace(/(https?:\/\/[^\s]+\.(?:jpg|jpeg|png|gif|webp|bmp|svg))/gi, (match, url, offset) => {
            if (isInsideTag(parsed, offset)) return match;
            return savePlaceholder(`<img src="${url}" alt="image" />`);
        });

        // URL diretti (matcha solo se non dentro tag HTML)
        parsed = parsed.replace(/(https?:\/\/[^\s<>"'()]+)/g, (match, url, offset) => {
            if (isInsideTag(parsed, offset)) return match;
            return savePlaceholder(`<a href="${url}" target="_blank">${url}</a>`);
        });

        // Ripristina i placeholder
        parsed = restorePlaceholders(parsed);

        // A capo
        parsed = parsed.replace(/\n/g, '<br>');

        return parsed;
    }

    /**
     * Formatta un oggetto Date in stringa orario (HH:MM)
     * @param {Date} date - Data da formattare
     * @returns {string} Stringa orario formattata
     * @private
     */
    formatTime(date) {
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `${hours}:${minutes}`;
    }

    /**
     * Formatta la dimensione di un file in modo leggibile
     * @param {number} bytes - Dimensione in bytes
     * @returns {string} Dimensione formattata (es. '1.5 MB')
     * @private
     */
    formatFileSize(bytes) {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }

    /**
     * Genera il tooltip per il bottone allega file con le restrizioni
     * @returns {string} Tooltip con informazioni sui tipi e dimensioni
     * @private
     */
    getFileButtonTooltip() {
        const baseTooltip = this.i18nTranslation('fileButtonTitle', 'Attach file');
        const restrictions = [];

        if (this.options.maxFileSize) {
            const sizeStr = this.formatFileSize(this.options.maxFileSize);
            const maxText = this.i18nTranslation('maxFileSize', 'Max: ') + sizeStr;
            restrictions.push(maxText);
        }

        if (this.options.allowedFileTypes) {
            const typesText = this.i18nTranslation('allowedTypes', 'Types: ') + this.options.allowedFileTypes;
            restrictions.push(typesText);
        }

        if (restrictions.length === 0) {
            return baseTooltip;
        }

        return baseTooltip + ' (' + restrictions.join(', ') + ')';
    }

    /**
     * Scrolla automaticamente al fondo dell'area messaggi
     * @private
     */
    scrollToBottom() {
        this.elements.messages.scrollTop = this.elements.messages.scrollHeight;
    }

    /**
     * Avvia una nuova conversazione
     * @param {boolean} [anonymous=false] - Se true, non genera un conversationId localmente
     * @returns {Promise<string|null>} ID della nuova conversazione, o null se anonima
     */
    async startConversation(anonymous = false) {
        this.messages = [];
        this.elements.messages.innerHTML = '';

        if (anonymous) {
            this.isAnonymous = true;
            this.conversationId = null;
        } else {
            this.isAnonymous = false;
            this.conversationId = this.generateId();
        }

        if (this.options.onNewConversation) {
            await this.options.onNewConversation(this.conversationId);
        }

        return this.conversationId;
    }

    /**
     * Riprende una conversazione esistente
     * @param {string} conversationId - ID della conversazione da riprendere
     * @param {Array} [messages=[]] - Messaggi precedenti della conversazione
     */
    async resumeConversation(conversationId, messages = []) {
        this.conversationId = conversationId;
        this.messages = messages;
        this.elements.messages.innerHTML = '';

        messages.forEach(msg => this.renderMessage(msg));
        this.scrollToBottom();

        if (this.options.onResumeConversation) {
            await this.options.onResumeConversation(conversationId);
        }
    }

    /**
     * Crea (se serve) un messaggio assistant "in attesa" per risposte non-streaming.
     * @returns {string} messageId
     * @private
     */
    startAssistantPendingMessage() {
        if (this.pendingMessageId) return this.pendingMessageId;

        const messageId = this.generateId();
        this.pendingMessageId = messageId;

        this.addMessage({
            id: messageId,
            author: 'assistant',
            content: '',
            timestamp: new Date(),
            pending: true
        });

        // Mostra subito l'indicatore anche se il contenuto è vuoto
        this.updateMessageContent(messageId, '', { showIndicator: true });

        return messageId;
    }

    /**
     * Finalizza il messaggio "in attesa" con il contenuto definitivo oppure lo rimuove in caso di fallback.
     * @param {string} content
     */
    finalizeAssistantPendingMessage(content) {
        const messageId = this.pendingMessageId;
        this.pendingMessageId = null;

        if (!messageId) return;

        // Aggiorna il DOM del messaggio pending e rimuovi indicatore
        this.updateMessageContent(messageId, content, { showIndicator: false });

        // Allinea anche lo stato in memoria (messages)
        const msg = this.messages.find(m => m && m.id === messageId);
        if (msg) {
            msg.content = content;
            msg.pending = false;
        }
    }

    /**
     * Verifica se la response contiene un header conversationId e lo aggiorna se l'istanza è anonima
     * @param {Response} response - Oggetto Response da controllare
     * @private
     */
    handleConversationIdHeader(response) {
        if (this.isAnonymous && !this.conversationId) {
            const conversationId = response.headers.get('x-conversationId');
            if (conversationId) {
                this.conversationId = conversationId;
                this.isAnonymous = false;

                if (this.options.onNewConversation) {
                    this.options.onNewConversation(this.conversationId);
                }
            }
        }
    }

    /**
     * Invia un messaggio al server
     * @param {string} text - Testo del messaggio
     * @param {File} [file=null] - File allegato opzionale
     * @returns {Promise<void>}
     */
    async sendMessage(text, file = null) {
        if (this.options.onSendMessage) {
            return await this.options.onSendMessage({
                conversationId: this.conversationId,
                text,
                file
            });
        }

        const formData = new FormData();
        if (this.conversationId) {
            formData.append('conversationId', this.conversationId);
        }
        formData.append('message', text);
        if (file) {
            formData.append('file', file);
        }

        // Mostra indicatore per richieste non-streaming (verrà rimpiazzato se la risposta è SSE)
        const pendingId = this.startAssistantPendingMessage();

        try {
            const response = await fetch(this.options.apiEndpoint, {
                method: 'POST',
                body: formData
            });

            if (!response.ok) throw new Error(`${response.status}: ${response.statusText}`);

            this.handleConversationIdHeader(response);

            const contentType = response.headers.get('content-type');
            const isStreaming = contentType && (contentType.includes('text/event-stream') || contentType.includes('application/x-ndjson') || contentType.includes('application/jsonl'));

            if (isStreaming) {
                // Se è streaming: rimuovi il pending e passa alla logica streaming “vera”
                this.pendingMessageId = null;
                const pendingEl = this.elements.messages.querySelector(`[data-message-id="${pendingId}"]`);
                if (pendingEl) pendingEl.remove();

                await this.handleStreamResponse(response);
            } else {
                const data = await response.json();
                const finalText = data.message || data.content || '';
                this.finalizeAssistantPendingMessage(finalText);
            }
        } catch (error) {
            console.error('Error sending message:', error);

            // Se avevamo un pending, trasformalo in errore (senza indicatore)
            this.finalizeAssistantPendingMessage(
                this.i18nTranslation('errorMessage', 'An error occurred. Please try again later.')
            );
        }
    }

    /**
     * Gestisce una risposta in streaming dal server
     * Supporta: SSE standard, OpenAI format, NDJSON, JSON chunks, Raw text
     * @param {Response} response - Risposta fetch con stream
     * @returns {Promise<void>}
     */
    async handleStreamResponse(response) {
        this.handleConversationIdHeader(response);

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        const contentType = response.headers.get('content-type') || '';

        const messageId = this.generateId();
        const message = {
            id: messageId,
            author: 'assistant',
            content: '',
            timestamp: new Date(),
            streaming: true
        };

        this.currentStreamingMessage = message;
        this.isStreaming = true;
        this.addMessage(message);

        let buffer = '';

        const extractContent = (parsed) => {
            if (typeof parsed === 'string') return parsed;
            if (parsed.choices && parsed.choices[0] && parsed.choices[0].delta) {
                return parsed.choices[0].delta.content || parsed.choices[0].delta.text || '';
            }
            if (parsed.choices && parsed.choices[0] && parsed.choices[0].text) {
                return parsed.choices[0].text;
            }
            return parsed.content || parsed.delta || parsed.message || parsed.output || parsed.text || '';
        };

        const isSSE = contentType.includes('text/event-stream');
        const isNDJSON = contentType.includes('application/x-ndjson') || contentType.includes('application/jsonl');

        try {
            while (true) {
                const {done, value} = await reader.read();

                if (done) {
                    this.isStreaming = false;
                    this.currentStreamingMessage = null;
                    this.updateMessageContent(messageId, message.content, { showIndicator: false, streaming: false });
                    const msg = this.messages.find(m => m && m.id === messageId);
                    if (msg) {
                        msg.streaming = false;
                    }
                    break;
                }

                buffer += decoder.decode(value, {stream: true});

                if (isSSE) {
                    const lines = buffer.split('\n');
                    buffer = lines.pop();

                    for (const line of lines) {
                        if (!line.trim()) continue;

                        let dataStr = line;

                        if (line.startsWith('data: ')) {
                            dataStr = line.slice(6);
                        } else if (line.startsWith('event:')) {
                            continue;
                        } else if (line.startsWith('id:')) {
                            continue;
                        }

                        if (dataStr === '[DONE]' || dataStr === '[DONE]\r') {
                            this.isStreaming = false;
                            this.currentStreamingMessage = null;
                            this.updateMessageContent(messageId, message.content, { showIndicator: false, streaming: false });
                            const msg = this.messages.find(m => m && m.id === messageId);
                            if (msg) {
                                msg.streaming = false;
                            }
                            break;
                        }

                        try {
                            const parsed = JSON.parse(dataStr);
                            const content = extractContent(parsed);
                            if (content) {
                                message.content += content;
                                this.updateStreamingMessage(messageId, message.content);
                            }
                        } catch (e) {
                            if (dataStr.trim() && !dataStr.startsWith('[')) {
                                message.content += dataStr;
                                this.updateStreamingMessage(messageId, message.content);
                            }
                        }
                    }
                } else if (isNDJSON) {
                    const lines = buffer.split('\n');
                    buffer = lines.pop();

                    for (const line of lines) {
                        if (!line.trim()) continue;
                        try {
                            const parsed = JSON.parse(line);
                            const content = extractContent(parsed);
                            if (content) {
                                message.content += content;
                                this.updateStreamingMessage(messageId, message.content);
                            }
                        } catch (e) {
                            if (line.trim()) {
                                message.content += line;
                                this.updateStreamingMessage(messageId, message.content);
                            }
                        }
                    }
                } else if (contentType.includes('application/json')) {
                    const lines = buffer.split('\n');
                    buffer = lines.pop();

                    for (const line of lines) {
                        if (!line.trim()) continue;
                        try {
                            const parsed = JSON.parse(line);
                            const content = extractContent(parsed);
                            if (content) {
                                message.content += content;
                                this.updateStreamingMessage(messageId, message.content);
                            }
                        } catch (e) {}
                    }
                } else {
                    message.content += buffer;
                    this.updateStreamingMessage(messageId, message.content);
                    buffer = '';
                }

                if (!this.isStreaming) break;
            }
        } catch (error) {
            console.error('Error reading stream:', error);
            this.isStreaming = false;
            this.currentStreamingMessage = null;
            this.updateMessageContent(messageId, message.content, { showIndicator: false, streaming: false });
            const msg = this.messages.find(m => m && m.id === messageId);
            if (msg) {
                msg.streaming = false;
            }
        }
    }

    /**
     * Aggiorna il contenuto di un messaggio in streaming
     * @param {string} messageId - ID del messaggio da aggiornare
     * @param {string} content - Nuovo contenuto del messaggio
     * @private
     */
    updateStreamingMessage(messageId, content) {
        this.updateMessageContent(messageId, content, { showIndicator: true, streaming: true });
    }

    /**
     * Aggiorna l'intera conversazione con nuovi messaggi
     * @param {Array} messages - Array di messaggi da visualizzare
     */
    updateConversation(messages) {
        this.messages = messages;
        this.elements.messages.innerHTML = '';
        messages.forEach(msg => this.renderMessage(msg));
        this.scrollToBottom();
    }

    /**
     * Genera un ID univoco
     * @returns {string} ID univoco generato
     * @private
     */
    generateId() {
        return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
    }

    /**
     * Restituisce l'ID della conversazione corrente
     * @returns {string|null} ID della conversazione
     */
    getConversationId() {
        return this.conversationId;
    }

    /**
     * Restituisce tutti i messaggi della conversazione corrente
     * @returns {Array} Array dei messaggi
     */
    getMessages() {
        return this.messages;
    }


    // ============================================================
    // DOWNLOAD CHAT (PDF/CSV/JSON)
    // ============================================================

    /**
     * Apre/chiude il menu download
     * @private
     */
    toggleDownloadMenu() {
        if (this.isDownloadMenuOpen) {
            this.closeDownloadMenu();
        } else {
            this.openDownloadMenu();
        }
    }

    /**
     * @private
     */
    openDownloadMenu() {
        this.isDownloadMenuOpen = true;
        this.elements.downloadMenu.classList.add('open');
        this.elements.downloadMenu.setAttribute('aria-hidden', 'false');
    }

    /**
     * @private
     */
    closeDownloadMenu() {
        this.isDownloadMenuOpen = false;
        if (!this.elements.downloadMenu) return;
        this.elements.downloadMenu.classList.remove('open');
        this.elements.downloadMenu.setAttribute('aria-hidden', 'true');
    }

    /**
     * Scarica file via Blob
     * @param {string} filename
     * @param {string|Blob} data
     * @param {string} mimeType
     * @private
     */
    downloadBlob(filename, data, mimeType) {
        const blob = (data instanceof Blob) ? data : new Blob([data], { type: mimeType });
        const url = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        a.remove();

        setTimeout(() => URL.revokeObjectURL(url), 2500);
    }

    /**
     * Ritorna stringa sicura per filename
     * @param {string} s
     * @returns {string}
     * @private
     */
    sanitizeFilename(s) {
        return String(s || '')
            .replace(/[\\\/:*?"<>|]+/g, '-')
            .replace(/\s+/g, ' ')
            .trim();
    }

    /**
     * Converte un messaggio in testo "senza markup" (niente markdown/html)
     * @param {string} markdown
     * @returns {string}
     * @private
     */
    toPlainText(markdown) {
        const html = this.parseContent(markdown || '');
        const doc = document.implementation.createHTMLDocument('');
        const container = doc.createElement('div');
        container.innerHTML = html;

        // Trasforma <br> in newline prima di textContent
        Array.prototype.slice.call(container.querySelectorAll('br')).forEach((br) => {
            br.parentNode.insertBefore(doc.createTextNode('\n'), br);
            br.parentNode.removeChild(br);
        });

        return (container.textContent || '').replace(/\r\n/g, '\n').trim();
    }

    /**
     * Escape per CSV (RFC4180-ish)
     * @param {string} value
     * @returns {string}
     * @private
     */
    csvEscape(value) {
        const s = String(value == null ? '' : value);
        const mustQuote = /[",\n\r]/.test(s);
        const escaped = s.replace(/"/g, '""');
        return mustQuote ? `"${escaped}"` : escaped;
    }

    /**
     * Scarica la chat in JSON (dump + metadati)
     */
    downloadChatAsJson() {
        const payload = {
            conversationId: this.conversationId,
            exportedAt: new Date().toISOString(),
            language: this.currentLanguage,
            title: this.options.title,
            messages: (this.messages || []).map((m) => ({
                id: m.id || null,
                author: m.author || null,
                content: m.content || '',
                timestamp: m.timestamp ? (new Date(m.timestamp)).toISOString() : null,
                streaming: Boolean(m.streaming),
                pending: Boolean(m.pending),
                file: m.file ? { name: m.file.name, type: m.file.type, size: m.file.size } : null
            }))
        };

        const name = this.sanitizeFilename(this.options.title || 'chat');
        const filename = `${name || 'chat'}-${this.conversationId || 'conversation'}.json`;
        this.downloadBlob(filename, JSON.stringify(payload, null, 2), 'application/json;charset=utf-8');
    }

    /**
     * Scarica la chat in CSV (solo informazioni, senza markup)
     * Colonne minime: autore, data, messaggio
     */
    downloadChatAsCsv() {
        const rows = [];
        rows.push(['autore', 'data', 'messaggio']); // richiesto: autore, data, messaggio

        (this.messages || []).forEach((m) => {
            const authorLabel =
                m.author === 'user'
                    ? this.i18nTranslation('userAuthorName', 'You')
                    : this.getAssistantDisplayName();

            const iso = m.timestamp ? (new Date(m.timestamp)).toISOString() : '';
            const plain = this.toPlainText(m.content || '');

            rows.push([authorLabel, iso, plain]);
        });

        const csv = rows
            .map((r) => r.map((c) => this.csvEscape(c)).join(','))
            .join('\n');

        const name = this.sanitizeFilename(this.options.title || 'chat');
        const filename = `${name || 'chat'}-${this.conversationId || 'conversation'}.csv`;
        this.downloadBlob(filename, csv, 'text/csv;charset=utf-8');
    }

    /**
     * "PDF" human readable: apre una finestra stampabile con HTML già renderizzato e avvia print.
     * Nota: il salvataggio in PDF dipende dal browser/OS (stampa -> "Salva come PDF").
     */
    downloadChatAsPdf() {
        const name = this.sanitizeFilename(this.options.title || 'Chat');
        const title = name || 'Chat';
        const exportedAt = new Date().toLocaleString();

        const itemsHtml = (this.messages || []).map((m) => {
            const authorLabel =
                m.author === 'user'
                    ? this.i18nTranslation('userAuthorName', 'You')
                    : this.getAssistantDisplayName();

            const time = m.timestamp ? (new Date(m.timestamp)) : null;
            const timeStr = time ? `${time.toLocaleDateString()} ${time.toLocaleTimeString()}` : '';

            // HTML "come UI": usa parseContent (markdown/html interpretati)
            const contentHtml = this.parseContent(m.content || '');

            return `
                <div class="msg ${m.author === 'user' ? 'user' : 'assistant'}">
                    <div class="meta">
                        <div class="author">${authorLabel}</div>
                        <div class="time">${timeStr}</div>
                    </div>
                    <div class="content">${contentHtml}</div>
                </div>
            `;
        }).join('\n');

        const html = `
        <!doctype html>
        <html lang="">
        <head>
          <meta charset="utf-8" />
          <title>${title}</title>
          <style>
            body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; margin: 24px; color: #212529; }
            h1 { font-size: 18px; margin: 0 0 6px; }
            .sub { color: #6c757d; font-size: 12px; margin: 0 0 16px; }
            .msg { margin: 0 0 14px; }
            .meta { display: flex; justify-content: space-between; font-size: 12px; margin: 0 0 4px; }
            .author { font-weight: 600; }
            .time { color: #6c757d; }
            .content { background: #fff; border: 1px solid rgba(0,0,0,0.08); padding: 10px 12px; border-radius: 8px; line-height: 1.5; }
            .msg.user .content { background: #e2e3e5; }
            img { max-width: 100%; height: auto; }
            pre { white-space: pre-wrap; word-wrap: break-word; }
            @media print { body { margin: 0; } }
          </style>
        </head>
        <body>
          <h1>${title}</h1>
          <p class="sub">ConversationId: ${this.conversationId || '-'} · Export: ${exportedAt}</p>
          ${itemsHtml || '<p class="sub">(nessun messaggio)</p>'}
          <script>
            window.focus();
            setTimeout(function(){ window.print(); }, 50);
          </script>
        </body>
        </html>`;

        const printWindow = window.open('', '_blank');
        if (!printWindow) return; // popup bloccato
        const doc = printWindow.document;
        doc.open();
        doc.close();

        const parser = new DOMParser();
        const parsedDoc = parser.parseFromString(html, 'text/html');
        doc.replaceChild(
            doc.importNode(parsedDoc.documentElement, true),
            doc.documentElement
        );

        printWindow.focus();
        setTimeout(() => printWindow.print(), 50);
    }
}

/**
 * Invia un messaggio a un'istanza di chatbot e restituisce la risposta.
 * Questa funzione crea un'istanza di ChatbotUI invisibile, invia il messaggio
 * e restituisce la risposta del chatbot senza visualizzare l'interfaccia.
 *
 * @function sendChatbotMessage
 * @async
 *
 * @param {string} apiEndpoint - Endpoint API per l'invio messaggi (es. '/api/chat')
 * @param {string|Object} message - Messaggio da inviare (stringa o oggetto JSON)
 * @param {Object} [options={}] - Opzioni aggiuntive
 * @param {string} [options.title='Chat'] - Titolo del chatbot
 * @param {string|null} [options.conversationId=null] - ID conversazione esistente
 * @param {Array} [options.messages=[]] - Messaggi precedenti della conversazione
 * @param {File|null} [options.file=null] - File da allegare al messaggio
 *
 * @returns {Promise<string>} Risposta del chatbot come stringa
 * @throws {Error} Se l'invio del messaggio fallisce o si verifica un timeout
 *
 * @example
 * // Invia messaggio testuale
 * const response = await sendChatbotMessage('/api/chat', 'Ciao, come stai?');
 * console.log(response);
 *
 * @example
 * // Invia messaggio JSON
 * const response = await sendChatbotMessage('/api/chat', {
 *     action: 'analyze',
 *     data: { text: 'Esempio' }
 * });
 *
 * @example
 * // Invia messaggio con file allegato
 * const fileInput = document.querySelector('input[type="file"]');
 * const file = fileInput.files[0];
 * const response = await sendChatbotMessage('/api/chat', 'Analizza questo file', {
 *     file: file
 * });
 * console.log(response);
 *
 * @example
 * // Con conversazione persistente
 * const response = await sendChatbotMessage('/api/chat', 'Continua', {
 *     conversationId: 'existing-id',
 *     messages: [
 *         { author: 'user', content: 'Primo messaggio', timestamp: new Date() },
 *         { author: 'assistant', content: 'Risposta', timestamp: new Date() }
 *     ]
 * });
 *
 * @example
 * // Gestione errori
 * try {
 *     const response = await sendChatbotMessage('/api/chat', 'Test');
 *     console.log('Risposta:', response);
 * } catch (error) {
 *     console.error('Errore:', error.message);
 * }
 */
async function sendChatbotMessage(apiEndpoint , message, options = {}) {
    const {
        title = 'Chat',
        conversationId = null,
        messages = [],
        file = null
    } = options;

    return new Promise(async (resolve, reject) => {
        let chatbotInstance = null;
        let resolved = false;

        const resolveOnce = (value) => {
            if (!resolved) {
                resolved = true;
                resolve(value);
            }
        };

        const rejectOnce = (error) => {
            if (!resolved) {
                resolved = true;
                reject(error);
            }
        };

        try {
            chatbotInstance = new ChatbotUI({
                title,
                apiEndpoint,
                onSendMessage: async (data) => {
                    try {
                        let response;

                        if (data.file) {
                            const formData = new FormData();
                            formData.append('conversationId', data.conversationId);
                            formData.append('message', data.text);
                            formData.append('file', data.file);

                            response = await fetch(apiEndpoint, {
                                method: 'POST',
                                body: formData
                            });
                        } else {
                            response = await fetch(apiEndpoint, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify(data)
                            });
                        }

                        if (!response.ok) {
                            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                        }

                        const contentType = response.headers.get('content-type') || '';
                        let fullResponse = '';

                        const extractContent = (parsed) => {
                            if (typeof parsed === 'string') return parsed;
                            if (parsed.choices && parsed.choices[0] && parsed.choices[0].delta) {
                                return parsed.choices[0].delta.content || parsed.choices[0].delta.text || '';
                            }
                            if (parsed.choices && parsed.choices[0] && parsed.choices[0].text) {
                                return parsed.choices[0].text;
                            }
                            return parsed.content || parsed.delta || parsed.message || parsed.output || parsed.text || '';
                        };

                        const isSSE = contentType.includes('text/event-stream');
                        const isNDJSON = contentType.includes('application/x-ndjson') || contentType.includes('application/jsonl');

                        if (isSSE || isNDJSON || contentType.includes('application/json')) {
                            const reader = response.body.getReader();
                            const decoder = new TextDecoder();
                            let buffer = '';

                            while (true) {
                                const { done, value } = await reader.read();
                                if (done) break;

                                buffer += decoder.decode(value, { stream: true });

                                if (isSSE) {
                                    const lines = buffer.split('\n');
                                    buffer = lines.pop();

                                    for (const line of lines) {
                                        if (!line.trim()) continue;
                                        let dataStr = line;

                                        if (line.startsWith('data: ')) {
                                            dataStr = line.slice(6);
                                        } else if (line.startsWith('event:') || line.startsWith('id:')) {
                                            continue;
                                        }

                                        if (dataStr === '[DONE]' || dataStr === '[DONE]\r') break;

                                        try {
                                            const parsed = JSON.parse(dataStr);
                                            fullResponse += extractContent(parsed);
                                        } catch (e) {
                                            if (dataStr.trim() && !dataStr.startsWith('[')) {
                                                fullResponse += dataStr;
                                            }
                                        }
                                    }
                                } else if (isNDJSON || contentType.includes('application/json')) {
                                    const lines = buffer.split('\n');
                                    buffer = lines.pop();

                                    for (const line of lines) {
                                        if (!line.trim()) continue;
                                        try {
                                            const parsed = JSON.parse(line);
                                            fullResponse += extractContent(parsed);
                                        } catch (e) {
                                            if (line.trim()) {
                                                fullResponse += line;
                                            }
                                        }
                                    }
                                }
                            }
                        } else {
                            let output;
                            try {
                                output = await response.clone().json();
                            } catch (e) {
                                output = await response.text();
                            }
                            fullResponse = output.message || output.content || output.output || output;
                        }

                        resolveOnce(fullResponse);
                    } catch (error) {
                        rejectOnce(error);
                    }
                }
            });

            chatbotInstance.elements.container.style.display = 'none';

            if (conversationId) {
                await chatbotInstance.resumeConversation(conversationId, messages);
            } else {
                await chatbotInstance.startConversation();
            }

            const messageText = typeof message === 'object' ? JSON.stringify(message) : message;
            await chatbotInstance.sendMessage(messageText, file);

            const checkResponse = setInterval(() => {
                const msgs = chatbotInstance.getMessages();
                const lastMsg = msgs[msgs.length - 1];
                if (lastMsg && lastMsg.author === 'assistant' && lastMsg.content) {
                    clearInterval(checkResponse);
                    resolveOnce(lastMsg.content);
                }
            }, 100);

            setTimeout(() => {
                clearInterval(checkResponse);
                if (!resolved) {
                    rejectOnce(new Error('Timeout waiting for response'));
                }
            }, 30000);

        } catch (error) {
            rejectOnce(error);
        }
    });
}

// Export per utilizzo come modulo
if (typeof module !== 'undefined' && module.exports) {
    module.exports = { ChatbotUI, sendChatbotMessage };
}